import posthog from 'posthog-js'
import { logger } from '@/utils/logger'
import { POSTHOG_LOADED_EVENT } from '@/providers/CSPostHogProvider'

// Field names and counts only, never values
export const FORM_FUNNEL_EVENTS = {
  loaded: 'Form Loaded',
  revealed: 'Form Revealed',
  started: 'Form Started',
  fieldCompleted: 'Form Field Completed',
  submitAttempted: 'Form Submit Attempted',
} as const

export type FormFunnelEventName = (typeof FORM_FUNNEL_EVENTS)[keyof typeof FORM_FUNNEL_EVENTS]

export interface FormFunnelContext {
  uid?: string
  form_id?: string
  portal_id?: string
  form_type?: string
  target?: string
}

export type FormFieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

const FIELD_SELECTOR = 'input, select, textarea'
const SUBMIT_SELECTOR = '.hs-submit input[type="submit"], .hs-submit button, input[type="submit"], button[type="submit"]'
const NON_FIELD_INPUT_TYPES = new Set(['hidden', 'submit', 'button', 'reset', 'image'])

interface PendingFormEvent {
  name: FormFunnelEventName
  properties: Record<string, unknown>
  timestamp: Date
}

const MAX_PENDING_EVENTS = 50
const pendingEvents: PendingFormEvent[] = []
let flushArmed = false

/** True once posthog.init has run. */
const isPostHogLoaded = (): boolean => Boolean((posthog as { __loaded?: boolean }).__loaded)

/** Sends everything buffered before init, oldest first. */
const flushPendingEvents = (): void => {
  flushArmed = false
  while (pendingEvents.length > 0) {
    const event = pendingEvents.shift() as PendingFormEvent
    // Original timestamp, so funnel order holds
    posthog.capture(event.name, event.properties, { timestamp: event.timestamp })
  }
}

/** Captures to PostHog, buffering until init since posthog-js drops earlier captures. */
export function captureFormEvent(
  name: FormFunnelEventName,
  properties: Record<string, unknown>,
  options?: { timestamp?: Date },
): void {
  try {
    const timestamp = options?.timestamp
    if (isPostHogLoaded()) {
      if (timestamp) {
        posthog.capture(name, properties, { timestamp })
      } else {
        posthog.capture(name, properties)
      }
      return
    }
    if (pendingEvents.length < MAX_PENDING_EVENTS) {
      pendingEvents.push({ name, properties, timestamp: timestamp ?? new Date() })
    }
    if (!flushArmed && typeof window !== 'undefined') {
      flushArmed = true
      window.addEventListener(POSTHOG_LOADED_EVENT, flushPendingEvents, { once: true })
    }
  } catch (error) {
    logger.warn('[FormFunnel] capture failed:', {
      event: name,
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

/** The input, select, or textarea behind an event target, or null. */
export function getFieldElement(target: EventTarget | null): FormFieldElement | null {
  if (!(target instanceof Element)) return null
  const el = target.closest(FIELD_SELECTOR)
  if (!el) return null
  if (el instanceof HTMLInputElement) {
    if (NON_FIELD_INPUT_TYPES.has(el.type)) return null
    return el
  }
  if (el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) return el
  return null
}

/** Whether the target is the form's submit control. */
export function isSubmitControl(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(SUBMIT_SELECTOR) !== null
}

/** Field name from the input, else HubSpot's hs_<name> container class, else the label. */
export function getFieldName(el: Element): string {
  const named = (el as Partial<FormFieldElement>).name
  if (typeof named === 'string' && named.trim()) return named.trim()

  const field = el.closest('.hs-form-field')
  if (field) {
    const hsClass = Array.from(field.classList).find((cls) => cls.startsWith('hs_') && cls.length > 3)
    if (hsClass) return hsClass.slice(3)
    const label = field.querySelector('label')?.textContent?.replace(/\*/g, '').trim()
    if (label) return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  }
  return 'unknown'
}

/** Fields the visitor can see; HubSpot renders hidden and progressive ones as display:none. */
export function getVisibleFields(form: Element): Element[] {
  return Array.from(form.querySelectorAll('.hs-form-field')).filter((field) => {
    if (!(field instanceof HTMLElement) || field.hidden) return false
    const style = getComputedStyle(field)
    return style.display !== 'none' && style.visibility !== 'hidden'
  })
}

/** Index among visible fields, or -1. */
export function getFieldIndex(el: Element, form: Element | null): number {
  const field = el.closest('.hs-form-field')
  if (!field || !form) return -1
  return getVisibleFields(form).indexOf(field)
}

/** Whether the field holds a value, without reading it. */
export function hasValue(el: FormFieldElement): boolean {
  if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) return el.checked
  return el.value.trim() !== ''
}

/** Names of the fields HubSpot is flagging as invalid. */
export function collectValidationErrors(form: Element): string[] {
  const names = new Set<string>()
  form.querySelectorAll('.hs-form-field').forEach((field) => {
    const flagged =
      field.querySelector('.hs-error-msgs, .hs-error-msg') !== null || field.querySelector('.error, .invalid') !== null
    if (!flagged) return
    const input = field.querySelector(FIELD_SELECTOR)
    names.add(input ? getFieldName(input) : getFieldName(field))
  })
  return Array.from(names)
}

const HUBSPOT_MESSAGE_HOSTS = ['hsforms.net', 'hsforms.com']

/** Same origin or a HubSpot form host. */
export function isTrustedHubSpotMessageOrigin(origin: string, pageOrigin: string): boolean {
  if (origin === pageOrigin) return true
  try {
    const host = new URL(origin).hostname
    return HUBSPOT_MESSAGE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))
  } catch {
    return false
  }
}

interface HubSpotCallbackMessage {
  type?: string
  eventName?: string
  id?: string
}

/** Whether a window message is HubSpot's onFormReady for this form. */
export function isFormReadyMessage(data: unknown, formId?: string): boolean {
  if (!data || typeof data !== 'object') return false
  const msg = data as HubSpotCallbackMessage
  if (msg.type !== 'hsFormCallback' || msg.eventName !== 'onFormReady') return false
  if (formId && msg.id !== formId) return false
  return true
}
